import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { LAYOUT_CONFIG } from '@/lib/config/layout'
import { MoveRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import Script from 'next/script'
import { Button } from '../../ui/button'
import { FooterMenu } from './footer-menu'
import { FooterMenuMobile } from './footer-menu-mobile'

export function Footer() {
  const isMobile = useIsMobileClient()

  return (
    <footer
      className='bg-neutral-900 px-5 py-8 lg:px-15 lg:py-6'
      data-tracking-level-1={'Footer'}
    >
      <div className='flex-wrap gap-y-5 lg:flex lg:items-center lg:justify-between'>
        <div className='lg:flex lg:flex-row-reverse lg:justify-between lg:gap-32 xl:gap-44'>
          <ul>
            <li className='flex items-center space-x-5'>
              <Image
                src={LAYOUT_CONFIG.FOOTER.contact.hotline.iconUrl}
                alt='Biểu tượng điện thoại liên hệ'
                width={34}
                height={34}
                className='lg:h-w-10.5 lg:w-10.5'
              />
              <div className='font-sans font-medium text-light'>
                <p className='text-sm leading-4'>Hotline</p>
                {LAYOUT_CONFIG.FOOTER.contact.hotline.numbers.map(
                  (number, idx) => (
                    <Link
                      href={`tel:${number}`}
                      className='whitespace-nowrap text-base lg:text-xl'
                      key={number}
                      prefetch={false}
                      data-tracking-level-2={'Hotline'}
                    >
                      {number} {idx === 0 && '- '}
                    </Link>
                  )
                )}

                <p className='lg:hidden'>
                  ({LAYOUT_CONFIG.FOOTER.contact.hotline.workingHours})
                </p>
              </div>
            </li>

            <li className='mt-5.5 flex items-center space-x-5 lg:mt-6 lg:space-x-4'>
              <Image
                src={LAYOUT_CONFIG.FOOTER.contact.email.iconUrl}
                alt='Biểu tượng email'
                width={34}
                height={34}
                className='lg:h-w-10.5 lg:w-10.5'
              />

              <div className='whitespace-nowrap font-sans font-medium text-light'>
                <p className='text-sm leading-4'>Email</p>
                <Link
                  href={`mailto:${LAYOUT_CONFIG.FOOTER.contact.email.address}`}
                  className='whitespace-nowrap text-base lg:text-xl'
                  data-tracking-level-2={'Email'}
                >
                  {LAYOUT_CONFIG.FOOTER.contact.email.address}
                </Link>
              </div>
            </li>
          </ul>
          <div className='my-6 border-y border-y-light/30 py-6 lg:col-span-2 lg:m-0 lg:max-w-[506px] lg:border-0 lg:py-0'>
            <h2 className='text-heading-6 text-light'>
              {LAYOUT_CONFIG.FOOTER.title}
            </h2>
            <p className='text-body-3 mt-1 text-neutral-100 lg:mt-1.5'>
              {LAYOUT_CONFIG.FOOTER.description}
            </p>
            <Button variant={'white'} className='group mt-4 lg:mt-5' asChild>
              <Link
                href={LAYOUT_CONFIG.FOOTER.feedback.link}
                data-tracking-level-2={'Feedback'}
                prefetch={false}
              >
                {LAYOUT_CONFIG.FOOTER.feedback.title}
                <MoveRight className='transition-transform group-hover:translate-x-1' />
              </Link>
            </Button>
          </div>
        </div>

        <ul className='flex items-center gap-8 max-lg:justify-between lg:gap-10'>
          {LAYOUT_CONFIG.FOOTER.socials.map((social) => (
            <li key={social.url}>
              <Link
                href={social.url}
                className='block w-fit transition-opacity hover:opacity-80'
                title={social.platform}
                target='_blank'
                rel='noopener noreferrer nofollow'
                data-tracking-level-2={social.platform}
                prefetch={false}
              >
                <Image
                  src={social.iconUrl}
                  alt={`Theo dõi chúng tôi trên ${social.platform}`}
                  width={40}
                  height={40}
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {isMobile ? (
        <div
          className='my-6 border-y border-y-light/30 py-6 lg:hidden'
          data-tracking-level-2={'Menu'}
        >
          <FooterMenuMobile />
        </div>
      ) : (
        <div className='max-lg:hidden' data-tracking-level-2={'Menu'}>
          <FooterMenu />
        </div>
      )}

      <div className='lg:px-8'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between'>
          <span className='text-body-4 text-light lg:text-neutral-300'>
            {LAYOUT_CONFIG.FOOTER.companyInfo.name} <br />
            <p className='mt-2 lg:mt-1'>
              {LAYOUT_CONFIG.FOOTER.companyInfo.licenseInfo}
            </p>
          </span>
          <ul className='mt-3 flex items-center space-x-7 lg:space-x-3'>
            {LAYOUT_CONFIG.FOOTER.companyInfo.certifications.map(
              (certification) => (
                <li key={certification.url}>
                  <Link
                    href={certification.url}
                    className='block'
                    title={certification.title}
                    prefetch={false}
                  >
                    <Image
                      src={certification.imageUrl}
                      alt={certification.title}
                      width={80}
                      height={30}
                      className='h-8 w-full object-contain lg:h-11'
                    />
                  </Link>
                </li>
              )
            )}
          </ul>
        </div>
      </div>
      <Script
        id='footer-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: LAYOUT_CONFIG.FOOTER.companyInfo.name,
            url: 'https://coolmate.me',
            logo: 'https://www.coolmate.me/images/logo.png',
            contactPoint: [
              {
                '@type': 'ContactPoint',
                telephone: LAYOUT_CONFIG.FOOTER.contact.hotline.numbers[0],
                contactType: 'customer service',
              },
            ],
            sameAs: LAYOUT_CONFIG.FOOTER.socials.map((social) => social.url),
          }),
        }}
      />
    </footer>
  )
}
